import { z } from 'zod';

import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin-only WhatsApp delivery log (read model).
 *
 * Agents never see a delivery failure in the chat widget - a red line under a
 * bubble is noise to someone who cannot act on it, and the usual cause
 * (closed window, bad number, template not approved) is an operations
 * problem. This log is where that lands instead: what failed, to whom, on
 * whose lead, and what the provider said.
 */

export const WhatsAppLogMessageItemSchema = z.object({
  id: z.string(),
  /** When the send was attempted. */
  at: z.string(),
  direction: z.enum(['IN', 'OUT']),
  kind: z.string(),
  /** RECEIVED | QUEUED | SENT | DELIVERED | READ | FAILED. */
  status: z.string(),
  /** The provider's own wording, verbatim - the point of the screen. */
  errorMessage: z.string().nullable(),
  templateName: z.string().nullable(),
  /** Message text as stored, trimmed for the grid. */
  preview: z.string().nullable(),
  phone: z.string(),
  contactName: z.string().nullable(),
  conversationId: z.string().nullable(),
  leadId: z.string().nullable(),
  leadReference: z.string().nullable(),
  /** Who owns the lead now - the person to chase about it. */
  leadAgentId: z.string().nullable(),
  leadAgentName: z.string().nullable(),
  /** Who pressed send, when a person did (null for automated sends). */
  sentById: z.string().nullable(),
  sentByName: z.string().nullable(),
});
export type WhatsAppLogMessageItem = z.infer<typeof WhatsAppLogMessageItemSchema>;

export const WhatsAppLogCallItemSchema = z.object({
  id: z.string(),
  at: z.string(),
  direction: z.enum(['INCOMING', 'OUTGOING']),
  /** INITIATED | IN_PROGRESS | END | REJECT | MISSED | FAILED. */
  status: z.string(),
  failedReason: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  phone: z.string(),
  contactName: z.string().nullable(),
  leadId: z.string().nullable(),
  leadReference: z.string().nullable(),
  leadAgentId: z.string().nullable(),
  leadAgentName: z.string().nullable(),
  /** The agent whose softphone handled it, when one did. */
  handledById: z.string().nullable(),
  handledByName: z.string().nullable(),
});
export type WhatsAppLogCallItem = z.infer<typeof WhatsAppLogCallItemSchema>;

export const WhatsAppLogQuerySchema = PaginationQuerySchema.extend({
  /**
   * Default view: only what went wrong (failed messages, missed / rejected /
   * failed calls). `false` shows everything.
   */
  onlyProblems: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .default(true),
  /** IST day bounds, YYYY-MM-DD, inclusive. */
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** The lead's current owner. */
  agentId: z.string().uuid().optional(),
});
export type WhatsAppLogQuery = z.infer<typeof WhatsAppLogQuerySchema>;

export const WhatsAppLogMessagesResponseSchema = paginatedResponseSchema(
  WhatsAppLogMessageItemSchema,
);
export type WhatsAppLogMessagesResponse = z.infer<typeof WhatsAppLogMessagesResponseSchema>;

export const WhatsAppLogCallsResponseSchema = paginatedResponseSchema(WhatsAppLogCallItemSchema);
export type WhatsAppLogCallsResponse = z.infer<typeof WhatsAppLogCallsResponseSchema>;
